import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Button } from '@/components/primitives/Button';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { useFadeUpOnScroll } from '@/hooks/useFadeUpOnScroll';
import { CONTACT_SALES_EMAIL, CONTACT_SALES_EMAIL_HREF } from '@/lib/contact';
import styles from './HeroSection.module.css';

export const HeroSection: FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  useFadeUpOnScroll(ref, { selector: '[data-fade]', stagger: 0.12 });

  return (
    <section ref={ref} className={styles.root} id="product">
      <Container size="default">
        <div className={styles.glow} aria-hidden="true" />
        <div className={styles.content}>
          <div data-fade>
            <Eyebrow tone="inverse">{t('home.hero.eyebrow')}</Eyebrow>
          </div>
          <h1 className={styles.title} data-fade>
            {t('home.hero.title')}
          </h1>
          <p className={styles.lede} data-fade>
            {t('home.hero.lede')}
          </p>
          <p className={styles.subtitle} data-fade>
            {t('home.hero.subtitle')}
          </p>
          <p className={styles.tagline} data-fade>
            {t('home.hero.tagline')}
          </p>
          <div className={styles.tags} data-fade>
            <span className={styles.tag}>🏦 {t('home.hero.tags.banking')}</span>
            <span className={styles.tag}>🚢 {t('home.hero.tags.trade')}</span>
            <span className={styles.tag}>🔬 {t('home.hero.tags.research')}</span>
          </div>
          <div className={styles.actions} data-fade>
            <Button as="link" to="/example" size="lg" variant="primary">
              {t('common.cta.example')}
            </Button>
            <Button as="link" to="/login" size="lg" variant="secondary">
              {t('common.cta.primary')}
            </Button>
          </div>
          <div className={styles.contactBadge} data-fade>
            <span className={styles.contactPrompt}>{t('home.hero.contactPrompt')}</span>
            <a className={styles.contactLink} href={CONTACT_SALES_EMAIL_HREF}>
              {CONTACT_SALES_EMAIL}
            </a>
          </div>
        </div>
      </Container>
    </section>
  );
};

HeroSection.displayName = 'HeroSection';
